import { PunchRoundAction, PunchRoundActionTypes } from '@completion/actions';
import { CheckSheetAssignment, McPackageSummary, PunchRound, PunchRoundScheduleResult } from '@completion/models';
export interface PunchRoundState {
  // list
  punchRounds: PunchRound[];
  selectedPunchRound: PunchRound | null;
  loading: boolean;
  sendingInvites: boolean;
  error: any;
  // form metadata
  availableMcps: McPackageSummary[];
  formMcpsLoading: boolean;
  mcpScope: CheckSheetAssignment[];
  scopeLoading: boolean;
  scheduleResult: PunchRoundScheduleResult | null;
}
export const initialState: PunchRoundState = {
  punchRounds: [],
  selectedPunchRound: null,
  loading: false,
  sendingInvites: false,
  error: null,
  availableMcps: [],
  formMcpsLoading: false,
  mcpScope: [],
  scopeLoading: false,
  scheduleResult: null,
};
export function punchRoundReducer(state: PunchRoundState = initialState, action: PunchRoundAction): PunchRoundState {
  switch (action.type) {
    // ── CRUD loading ────────────────────────────────────────────
    case PunchRoundActionTypes.LoadPunchRounds:
    case PunchRoundActionTypes.LoadPunchRoundById:
    case PunchRoundActionTypes.CreatePunchRound:
    case PunchRoundActionTypes.UpdatePunchRound:
    case PunchRoundActionTypes.UpdatePunchRoundCheckSheetAssignmentStatus:
    case PunchRoundActionTypes.DeletePunchRound:
      return { ...state, loading: true, error: null };
    case PunchRoundActionTypes.LoadPunchRoundsSuccess:
      return { ...state, loading: false, punchRounds: action.punchRounds };
    case PunchRoundActionTypes.LoadPunchRoundByIdSuccess: {
      const fresh = action.punchRound;
      return {
        ...state,
        loading: false,
        punchRounds: state.punchRounds.some((r) => r.id === fresh.id)
          ? state.punchRounds.map((r) => (r.id === fresh.id ? fresh : r))
          : [...state.punchRounds, fresh],
        selectedPunchRound: fresh,
      };
    }
    case PunchRoundActionTypes.CreatePunchRoundSuccess:
      return {
        ...state,
        loading: false,
        punchRounds: [...state.punchRounds, action.punchRound],
        selectedPunchRound: action.punchRound,
      };
    case PunchRoundActionTypes.UpdatePunchRoundSuccess: {
      const updated = action.punchRound;
      return {
        ...state,
        loading: false,
        punchRounds: state.punchRounds.map((r) => (r.id === updated.id ? updated : r)),
        selectedPunchRound: state.selectedPunchRound?.id === updated.id ? updated : state.selectedPunchRound,
      };
    }
    case PunchRoundActionTypes.UpdatePunchRoundCheckSheetAssignmentStatusSuccess:
      return { ...state, loading: false };
    case PunchRoundActionTypes.DeletePunchRoundSuccess:
      return {
        ...state,
        loading: false,
        punchRounds: state.punchRounds.filter((r) => r.id !== action.punchRoundId),
        selectedPunchRound: state.selectedPunchRound?.id === action.punchRoundId ? null : state.selectedPunchRound,
      };
    case PunchRoundActionTypes.LoadPunchRoundsFailure:
    case PunchRoundActionTypes.LoadPunchRoundByIdFailure:
    case PunchRoundActionTypes.CreatePunchRoundFailure:
    case PunchRoundActionTypes.UpdatePunchRoundFailure:
    case PunchRoundActionTypes.UpdatePunchRoundCheckSheetAssignmentStatusFailure:
    case PunchRoundActionTypes.DeletePunchRoundFailure:
      return { ...state, loading: false, error: action.error };
    case PunchRoundActionTypes.SelectPunchRound:
      return { ...state, selectedPunchRound: action.punchRound };
    case PunchRoundActionTypes.ClearPunchRound:
      return { ...state, selectedPunchRound: null };
    case PunchRoundActionTypes.ClearPunchRounds:
      return { ...initialState };
    // ── Invites ────────────────────────────────────────────────
    case PunchRoundActionTypes.SendPunchRoundInvites:
      return { ...state, sendingInvites: true, error: null };
    case PunchRoundActionTypes.SendPunchRoundInvitesSuccess: {
      const updated = action.punchRound;
      // Backend may return 204 No Content (null body) — just reset the sending flag.
      if (!updated) {
        return { ...state, sendingInvites: false };
      }
      return {
        ...state,
        sendingInvites: false,
        punchRounds: state.punchRounds.map((r) => (r.id === updated.id ? updated : r)),
        selectedPunchRound: state.selectedPunchRound?.id === updated.id ? updated : state.selectedPunchRound,
      };
    }
    case PunchRoundActionTypes.SendPunchRoundInvitesFailure:
      return { ...state, sendingInvites: false, error: action.error };
    case PunchRoundActionTypes.FindPunchRoundSchedule:
      return { ...state, scheduleResult: null };
    case PunchRoundActionTypes.FindPunchRoundScheduleSuccess:
      return { ...state, scheduleResult: action.result };
    case PunchRoundActionTypes.FindPunchRoundScheduleFailure:
      return { ...state, scheduleResult: null, error: action.error };
    // ── Form metadata ────────────────────────────────────────────
    case PunchRoundActionTypes.LoadPunchRoundFormMcps:
      return { ...state, formMcpsLoading: true };
    case PunchRoundActionTypes.LoadPunchRoundFormMcpsSuccess:
      return { ...state, formMcpsLoading: false, availableMcps: action.mcps };
    case PunchRoundActionTypes.LoadPunchRoundFormMcpsFailure:
      return { ...state, formMcpsLoading: false, error: action.error };
    case PunchRoundActionTypes.LoadPunchRoundMcpScope:
      return { ...state, scopeLoading: true, mcpScope: [] };
    case PunchRoundActionTypes.LoadPunchRoundMcpScopeSuccess:
      return { ...state, scopeLoading: false, mcpScope: action.csas };
    case PunchRoundActionTypes.LoadPunchRoundMcpScopeFailure:
      return { ...state, scopeLoading: false, error: action.error };
    case PunchRoundActionTypes.ClearPunchRoundFormData:
      return { ...state, availableMcps: [], mcpScope: [], formMcpsLoading: false, scopeLoading: false };
    default:
      return state;
  }
}
